import { useEffect } from "react";
import dynamic from "next/dynamic";
import { useRouter } from "next/compat/router";
import NextLink from "next/link";
import { Box, Button, IconFa, Loader } from "@cruk/cruk-react-components";
import { useInView } from "react-intersection-observer";
import { faQrcode, faShareAlt } from "@fortawesome/free-solid-svg-icons";

import { useSwrGet } from "@fwa/src/hooks/useSwrGet";
import { queryAsString } from "@fwa/src/utils/urlUtils";
import { userNameForPage, firstNameForPage } from "@fwa/src/utils/pageUtils";
import {
  fwsUrlFundraisingPageSlug,
  updatePage,
} from "@fwa/src/services/fundraisingPageService";
import { useFundraiserContext } from "@fwa/src/contexts/FundraiserContext";
import { useTracking } from "@fwa/src/hooks/useTracking";
import { isPast, isToday } from "@fwa/src/utils/timeUtils";
import { useModalContext } from "@fwa/src/contexts/ModalContext";
import { sendGTMTrackingEvent } from "@fwa/src/services/gtm";
import { imagePathFromImageType } from "@fwa/src/services/imageService";
import { useOptimizelyContext } from "@fwa/src/contexts/OptimizelyContext";
import { useMounted } from "@fwa/src/hooks/useMounted";

import { EditableInMemoryForm } from "@fwa/src/components/EditableInMemoryForm";
import { ErrorPageWrapper } from "@fwa/src/components/ErrorPageWrapper";
import { FundraisingPageActivityBlock } from "@fwa/src/components/FundraisingPageActivityBlock";
import { FeedItemShared } from "@fwa/src/components/FeedItemShared";
import { FundraisingPageAdmin } from "@fwa/src/components/FundraisingPageAdmin";
import { RestrictionSection } from "@fwa/src/components/RestrictionSection";
import { Share } from "@fwa/src/components/Share";
import { PageHeaderImage } from "@fwa/src/components/PageHeaderImage";
import { StravaSection } from "@fwa/src/components/StravaSection";
import { FitbitSection } from "@fwa/src/components/FitbitSection";
import { FundraisingPageTotaliser } from "@fwa/src/components/FundraisingPageTotaliser";
import { TotaliserImpactCarousel } from "@fwa/src/components/TotaliserImpactCarousel";
import { FundraisingPageLatestUpdates } from "@fwa/src/components/FundraisingPageLatestUpdates";
import { FundraisingPageClosedInfo } from "@fwa/src/components/FundraisingPageClosedInfo";
import { ErrorBoundary } from "@fwa/src/components/ErrorBoundary";

import { PageTitle } from "@fwa/src/components/PageTitle";
import { PageSubtitle } from "@fwa/src/components/PageSubtitle";
import { PageStory } from "@fwa/src/components/PageStory";
import { EditableDisplayNameForm } from "@fwa/src/components/EditableDisplayNameForm";
import { FundraisingPageVideo } from "@fwa/src/components/FundraisingPageVideo";
import { QrModal } from "@fwa/src/components/QrModal";
import { EmailJourneyModalManager } from "@fwa/src/components/EmailJourneyModalManager";
import { ActivityManagementJourneyModalManager } from "@fwa/src/components/ActivityManagementJourneyModalManager";
import { EventManagementJourneyModalManager } from "@fwa/src/components/EventManagementJourneyModalManager";

import styles from "./styles.module.css";
import sharedStyles from "@fwa/src/components/styles.module.css";

import {
  type FeedItemType,
  type FundraisingPageType,
  type PageType,
  type RestrictionType,
} from "@fwa/src/types";

const Gallery = dynamic(() => import("@fwa/src/components/Gallery"), {
  ssr: false,
});
const FundraisingPageSettings = dynamic(
  () => import("@fwa/src/components/FundraisingPageSettings"),
  { ssr: false }, // has local date string
);
const FundraisingPageModalsManager = dynamic(
  () => import("@fwa/src/components/FundraisingPageModalsManager"),
);
const FitbitEndOfLifeWarning = dynamic(
  () => import("@fwa/src/components/FitbitEndOfLifeWarning"),
);
const FitbitConnectionError = dynamic(
  () => import("@fwa/src/components/FitbitConnectionError"),
);
const FacebookConnectionError = dynamic(
  () => import("@fwa/src/components/FacebookConnectionError"),
);
const ToastMessages = dynamic(
  () => import("@fwa/src/components/ToastMessages"),
);
const FundraisingPageToolbar = dynamic(
  () => import("@fwa/src/components/FundraisingPageToolbar"),
);

export const FundraisingPage = ({
  pageData,
  sharedFeedItemData,
}: {
  pageData: FundraisingPageType;
  sharedFeedItemData?: FeedItemType;
}) => {
  const isMounted = useMounted();
  const [fundraiserState] = useFundraiserContext();
  const { trackEventOptimizely } = useOptimizelyContext();
  const { trackEventGtm, trackError } = useTracking();
  const router = useRouter();
  //  figure out unique page url for page cacheId
  const { slug, feed } = router?.query || {};
  const slugString = queryAsString(slug);
  const feedString = queryAsString(feed);
  const feedId = sharedFeedItemData?.uniqueId || feedString || "";
  const [modalState, setModalState] = useModalContext();

  const { ref: feedRef, inView: feedInView } = useInView({
    triggerOnce: true,
    rootMargin: "200px",
  });
  const { ref: galleryRef, inView: galleryInView } = useInView({
    triggerOnce: true,
    rootMargin: "200px",
  });

  const url = slugString.length
    ? `${fwsUrlFundraisingPageSlug({ slug: slugString })}`
    : null;
  // page cache

  const canFetchPageData =
    isMounted && fundraiserState.loggedInStatus !== "unknown";
  const authenticatedCacheScope =
    fundraiserState.loggedInStatus === "loggedIn"
      ? `fundraiser:${fundraiserState.fundraiser?.uniqueId || fundraiserState.oauthUser?.uniqueId || "authenticated"}`
      : undefined;
  const opts = { fallbackData: pageData };
  const {
    data: swrPage,
    error,
    mutate,
  } = useSwrGet<FundraisingPageType>(
    canFetchPageData ? url : null,
    opts,
    authenticatedCacheScope,
  );
  const page = isMounted ? (swrPage ?? pageData) : pageData;
  const pageFundraiser = page?.fundraiser;
  // Used updateing an attribute on the page object on FWS API and then mutating the local page object
  const updateAndMutatePage = async (
    partialData: Partial<FundraisingPageType>,
    refresh?: boolean,
  ) => {
    try {
      if ("activityName" in partialData) {
        trackEventOptimizely({ eventKey: "activity_name_updated" });
      }
      if ("activityDateTime" in partialData) {
        trackEventOptimizely({ eventKey: "activity_date_updated" });
      }
      if ("title" in partialData) {
        trackEventOptimizely({ eventKey: "title_updated" });
      }
      if ("videoUrl" in partialData) {
        trackEventOptimizely({ eventKey: "video_updated" });
      }
      if ("story" in partialData) {
        trackEventOptimizely({ eventKey: "story_updated" });
      }
    } catch {
      // nothing
    }

    return updatePage({ pageId: page?.uniqueId || "", data: partialData })
      .then(
        (updatedPageData) =>
          mutate(
            updatedPageData,
            refresh || false,
          ) as Promise<FundraisingPageType>,
      )
      .catch((err) => {
        trackError(err as Error, { component: "FundraisingPage" });
      });
  };

  // this is when we want to mutate the page but the data is set via an alternative api endpoint
  // for example setting the header image which has its own endpoint to create an image entity before attaching to a page
  // but header image data still appears in page data so we can't use updateAndMutatePage.
  const mutatePage = (partialData: Partial<FundraisingPageType>) =>
    mutate({ ...page, ...partialData }, false) as Promise<
      undefined | void | FundraisingPageType
    >;

  const canEdit: boolean =
    !!fundraiserState?.fundraiser?.uniqueId &&
    fundraiserState?.fundraiser?.uniqueId === page?.fundraiser?.uniqueId;
  const isStravaMetric = page?.strava?.useMetric ?? true;
  const fitbitTargetSteps = page?.fitbit?.targetSteps || 0;
  const fitbitDuration =
    page?.fitbit?.startDate && page?.fitbit?.endDate
      ? Math.round(
          (new Date(`${page?.fitbit?.endDate}`).getTime() -
            new Date(`${page?.fitbit?.startDate}`).getTime()) /
            (1000 * 60 * 60 * 24),
        ) + 1
      : 0;
  const fitbitDailyTargetSteps = fitbitTargetSteps
    ? fitbitTargetSteps / fitbitDuration
    : 0;
  const fitbitValid = fundraiserState?.fundraiser?.fitbitUser?.valid ?? true;
  const isFacebookUserValid =
    fundraiserState?.fundraiser?.facebookUser?.valid ?? true;
  const isFacebookFundraiserValid =
    page?.facebookFundraiser && canEdit ? page.facebookFundraiser.valid : true;

  const userName = page ? userNameForPage(page) : "";
  const firstName = page ? firstNameForPage(page) : "";

  const avatarUrl =
    !page?.owner && page?.fundraiser?.profileImage
      ? page.fundraiser.profileImage?.entityType === "ImageFacebook"
        ? `${page.fundraiser?.profileImage.url}?width=300&height=300`
        : imagePathFromImageType(page.fundraiser?.profileImage)
      : "";

  const restriction: RestrictionType | null =
    page?.restriction ||
    page?.fundraisingPageType?.restriction ||
    page?.brand?.restriction ||
    page?.fundraisingPageType?.brand?.restriction ||
    null;

  const pageCloseDate = page?.closeDate ? new Date(page.closeDate) : null;
  const isPageClosed: boolean =
    page?.pageStatus === "closed" ||
    (pageCloseDate && (isToday(pageCloseDate) || isPast(pageCloseDate))) ||
    false;

  const handleShowQrClick = () => {
    if (modalState || !page) return;
    setModalState(<QrModal />);
    trackEventOptimizely({ eventKey: "of_show_qr_code_modal" });
  };

  // If logged and canEdit in refresh because we might have non logged in data cached on load
  // this might be missing props like latestMilestone refetch

  useEffect(() => {
    const hasLogginInPageData =
      pageFundraiser && "emailAddress" in pageFundraiser;
    if (
      fundraiserState.loggedInStatus === "loggedIn" &&
      !hasLogginInPageData &&
      canEdit
    ) {
      mutate().catch((err) => {
        trackError(err as Error, { component: "FundraisingPage" });
      });
    }
  }, [fundraiserState, pageFundraiser, trackError, mutate, canEdit]);

  useEffect(() => {
    sendGTMTrackingEvent({
      event: "pageView",
      pagePath: "/fundraising-page",
      pageTitle: "fundraising page",
    });
  }, []);

  return (
    <ErrorPageWrapper error={error!}>
      {/* Display modal */}
      {modalState}

      {canEdit && <ToastMessages />}
      {/* Loading state */}
      {!page ? (
        <Loader />
      ) : (
        <>
          {/* Data state */}
          <div
            className={[
              sharedStyles["content-max-width"],
              "spacing-props",
            ].join(" ")}
            data-margin-bottom="xxl"
          >
            {/* Looks for from=email in the querystring */}
            <EmailJourneyModalManager page={page} />
            {/* Looks for signature in the querystring */}
            <ActivityManagementJourneyModalManager page={page} />
            {/* Looks for em=1 in the querystring */}
            <EventManagementJourneyModalManager page={page} />

            {canEdit && page?.facebookFundraiser && (
              <FacebookConnectionError
                page={page}
                isValid={isFacebookUserValid && isFacebookFundraiserValid}
              />
            )}
            {canEdit && page?.fitbit && (
              <>
                <FitbitEndOfLifeWarning />
                <FitbitConnectionError page={page} isValid={fitbitValid} />
              </>
            )}

            {canEdit && <FundraisingPageModalsManager page={page} />}

            {/* Bottom action bar */}
            {canEdit ? (
              <FundraisingPageToolbar
                page={page}
                canEdit={canEdit}
                isPageClosed={isPageClosed}
              />
            ) : (
              <Box
                className={[
                  styles["action-button-wrapper"],
                  "component-box",
                  "spacing-props",
                ].join(" ")}
                paddingHorizontal="s"
                paddingVertical="xxs"
              >
                <div className={sharedStyles["content-max-width"]}>
                  <div
                    className={styles["action-button-flex"]}
                    data-component="page-bottom-ctas"
                  >
                    <Share
                      page={page as PageType}
                      feedItem={null}
                      isOwner={canEdit}
                      popoverPosition="top"
                      fixedPosition={true}
                      ctaLocation="bottom_bar"
                    >
                      <Button
                        type="button"
                        appearance="secondary"
                        data-cta-type="open-share"
                      >
                        <IconFa faIcon={faShareAlt} />
                        Share
                      </Button>
                    </Share>
                    {!isPageClosed && (
                      <NextLink
                        href={`/donate/${page.uniqueId}/details`}
                        onClick={() => {
                          trackEventGtm({
                            event: "donate_cta",
                            cta: "bottom_bar",
                          });
                        }}
                        data-cta-type="donate"
                      >
                        <Button appearance="primary" as="span">
                          Donate
                        </Button>
                      </NextLink>
                    )}
                  </div>
                </div>
              </Box>
            )}

            {/* Top actions block */}
            <Box backgroundColor="background-light" padding="s" margin="none">
              <div
                className={sharedStyles["row-stretch"]}
                data-component="page-actions-top"
              >
                <Share
                  page={page}
                  feedItem={null}
                  isOwner={canEdit}
                  popoverPosition="bottom"
                  ctaLocation="header"
                >
                  <Button
                    type="button"
                    appearance="secondary"
                    data-cta-type="open-share"
                  >
                    <IconFa faIcon={faShareAlt} />
                    Share
                  </Button>
                </Share>
                {!isPageClosed && (
                  <NextLink
                    href={`/donate/${page.uniqueId}/details`}
                    onClick={() => {
                      trackEventGtm({
                        event: "donate_cta",
                        cta: "header",
                      });
                    }}
                    data-cta-type="donate"
                  >
                    <Button appearance="primary" as="span">
                      Donate
                    </Button>
                  </NextLink>
                )}
              </div>
            </Box>
            {/* Shared feedItem */}
            {(sharedFeedItemData || !!feedString.length) && (
              <FeedItemShared
                feedId={feedId}
                pageId={page.uniqueId}
                pageUrl={page.url}
                isStravaMetric={isStravaMetric}
                fitbitDailyTargetSteps={fitbitDailyTargetSteps}
                sharedFeedItemData={sharedFeedItemData}
              />
            )}

            <Box marginBottom="m">
              <Box
                backgroundColor="background-light"
                padding="none"
                margin="none"
                data-component="page-display-name"
              >
                <div className={sharedStyles["avatar-responsive-wrapper"]}>
                  <EditableDisplayNameForm
                    avatarUrl={avatarUrl}
                    canEdit={canEdit}
                    fieldName="display name"
                    handleEditData={updateAndMutatePage}
                    userName={
                      page.owner?.displayName
                        ? page.owner.displayName
                        : userName
                    }
                    page={page}
                  />
                </div>
              </Box>
              {/* TitleBlock */}
              <div className={sharedStyles["header-group"]}>
                <PageTitle
                  canEdit={canEdit}
                  page={page}
                  updateAndMutatePage={updateAndMutatePage}
                />
                {/* Header image  */}
                <Box marginBottom="none" data-component="page-header-image">
                  <PageHeaderImage
                    canEdit={canEdit}
                    page={page}
                    mutatePage={mutatePage}
                  />
                </Box>
              </div>

              <Box
                backgroundColor="background-light"
                padding="s"
                margin="none"
                paddingBottom="xxl"
              >
                {/* Totaliser */}
                <Box marginBottom="xs" data-component="page-totaliser">
                  <FundraisingPageTotaliser
                    canEdit={canEdit}
                    page={page}
                    updateAndMutatePage={updateAndMutatePage}
                  />
                </Box>
                {restriction === null ? (
                  <TotaliserImpactCarousel page={page} />
                ) : null}
                <div
                  className={styles["totaliser-buttons-flex"]}
                  data-component="page-totaliser-ctas"
                >
                  <Share
                    page={page}
                    feedItem={null}
                    isOwner={canEdit}
                    popoverPosition="top"
                    ctaLocation="totaliser"
                  >
                    <Button
                      type="button"
                      appearance="secondary"
                      data-cta-type="open-share"
                    >
                      <IconFa faIcon={faShareAlt} />
                      Share
                    </Button>
                  </Share>
                  {!isPageClosed && (
                    <NextLink
                      href={`/donate/${page.uniqueId}/details`}
                      onClick={() => {
                        trackEventGtm({
                          event: "donate_cta",
                          cta: "totaliser",
                        });
                      }}
                      data-cta-type="donate"
                    >
                      <Button appearance="primary" as="span">
                        Donate
                      </Button>
                    </NextLink>
                  )}
                </div>
                <div className={sharedStyles["row-align-center"]}>
                  <Button
                    appearance="tertiary"
                    onClick={handleShowQrClick}
                    data-cta-type="open-qr-code"
                  >
                    <Box as="span">
                      <IconFa faIcon={faQrcode} />
                    </Box>
                    QR code
                  </Button>
                </div>
              </Box>

              {/* ActivityBlock */}
              <FundraisingPageActivityBlock
                canEdit={canEdit}
                page={page}
                updateAndMutatePage={updateAndMutatePage}
              />

              {/* Page connections settings section */}
              {canEdit && (
                <Box marginVertical="m">
                  <FundraisingPageSettings
                    page={page}
                    updateAndMutatePage={updateAndMutatePage}
                  />
                </Box>
              )}
              {isPageClosed && (
                <Box marginBottom="m" data-component="page-closed-info">
                  <FundraisingPageClosedInfo
                    fundraiserId={page.fundraiser.uniqueId}
                    userName={userName}
                  />
                </Box>
              )}
            </Box>

            <div className={styles["column-wrapper"]}>
              <div className={styles["desktop-right"]}>
                {/* connections, enhance section  */}
                {canEdit && (
                  <Box marginBottom="m">
                    <FundraisingPageAdmin page={page} mutatePage={mutatePage} />
                  </Box>
                )}
                {/* Activity trackers */}
                {(page.strava || page.fitbit) && (
                  <Box backgroundColor="background-light" padding="s">
                    {page.strava && (
                      <Box data-component="strava-tracker">
                        <StravaSection
                          canEdit={canEdit}
                          strava={page.strava}
                          mutatePage={mutatePage}
                        />
                      </Box>
                    )}
                    {page.strava && page.fitbit && (
                      <Box>
                        <div className={sharedStyles["divider-light"]} />
                      </Box>
                    )}
                    {page.fitbit && (
                      <Box data-component="fitbit-tracker">
                        <FitbitSection
                          canEdit={canEdit}
                          fitbit={page.fitbit}
                          mutatePage={mutatePage}
                        />
                      </Box>
                    )}
                  </Box>
                )}
                {/* subtitle and story block */}
                <Box backgroundColor="background-light" padding="s">
                  <PageSubtitle
                    page={page}
                    canEdit={canEdit}
                    updateAndMutatePage={updateAndMutatePage}
                  />

                  <PageStory
                    page={page}
                    updateAndMutatePage={updateAndMutatePage}
                    canEdit={canEdit}
                  />
                  {/* In memory section */}
                  <ErrorBoundary>
                    <EditableInMemoryForm
                      page={page}
                      name={page.activityInMemory}
                      handleEditData={updateAndMutatePage}
                      canEdit={canEdit}
                    />
                  </ErrorBoundary>
                  <FundraisingPageVideo
                    page={page}
                    canEdit={canEdit}
                    updateAndMutatePage={updateAndMutatePage}
                  />

                  <div id="gallery" ref={galleryRef}>
                    {galleryInView && (
                      <Gallery canEdit={canEdit} pageId={page.uniqueId} />
                    )}
                  </div>
                </Box>
                <Box marginBottom="m">
                  <RestrictionSection restriction={restriction} />
                </Box>
              </div>

              <div className={styles["desktop-left"]}>
                <div ref={feedRef} id="post">
                  {/* // id=post set to allow progress section to scroll as feed section
                  may not be loaded. */}
                  <FundraisingPageLatestUpdates
                    canEdit={canEdit}
                    page={page}
                    url={url}
                    feedInView={feedInView}
                    isStravaMetric={isStravaMetric}
                    fitbitDailyTargetSteps={fitbitDailyTargetSteps}
                    firstName={firstName}
                  />
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </ErrorPageWrapper>
  );
};

export default FundraisingPage;
